import { useTranslation } from 'react-i18next';
import { Auth } from 'aws-amplify';
import { useState } from 'react';
import React from 'react';

import { useForgotPasswordForm } from './form';
import { useInfoPopup } from 'hooks';
import { getAmplifyAuthErrorMessage, CognitoError } from '../utils';
import { checkUserForForgotPassword } from 'services/auth';

type SocialProvider = 'google' | 'apple' | 'facebook';

const normalizeProviders = (providers: string[] = []): SocialProvider[] => {
  const normalized = providers.map((p) => p.toLowerCase());
  const result: SocialProvider[] = [];
  if (normalized.some((p) => p.includes('google'))) result.push('google');
  if (normalized.some((p) => p.includes('apple'))) result.push('apple');
  if (normalized.some((p) => p.includes('facebook'))) result.push('facebook');
  return result;
};

export const useForgotPassword = () => {
  const { t } = useTranslation();
  const { handleSubmit, setValue, ...form } = useForgotPasswordForm();
  const [isLoading, setIsLoading] = useState(false);
  const [authError, setAuthError] = useState<React.ReactNode>(null);
  const [highlightProviders, setHighlightProviders] = useState<
    SocialProvider[]
  >([]);
  const [showSignupCta, setShowSignupCta] = useState(false);
  const clearAuthError = () => setAuthError(null);
  const clearHighlights = () => setHighlightProviders([]);
  const clearSignupCta = () => setShowSignupCta(false);
  const { showInfoPopup } = useInfoPopup();

  const onFailure = (error: CognitoError) => {
    const title = getAmplifyAuthErrorMessage(t, error);
    setAuthError(title);
  };

  const onSubmit = handleSubmit(async ({ email }) => {
    try {
      clearAuthError();
      clearHighlights();
      clearSignupCta();
      setIsLoading(true);
      const normalizedEmail = email.toLowerCase();

      const check = await checkUserForForgotPassword(normalizedEmail);
      if (check.status === 'NOT_FOUND') {
        setAuthError(t('auth.userDoesNotExist'));
        setShowSignupCta(true);
        return;
      }

      if (check.status === 'NOT_VERIFIED') {
        setAuthError(t('auth.verification_message'));
        return;
      }

      if (check.status === 'SOCIAL_LOGIN') {
        setHighlightProviders(
          normalizeProviders(check.socialLoginProviders || []),
        );
        const providers =
          check.socialLoginProviders.length > 0
            ? check.socialLoginProviders.join(', ')
            : null;

        setAuthError(
          providers
            ? t('auth.social_login_required', { providers })
            : t('auth.socialLoginDetected.message'),
        );
        return;
      }

      await Auth.forgotPassword(normalizedEmail);

      showInfoPopup({
        type: 'success',
        title: t('auth.codeSended'),
      });
    } catch (error: any) {
      // If the pre-check fails (network/server), fall back to existing Amplify error handling.
      if (error?.isAxiosError) {
        setAuthError(t('errors.text500'));
        return;
      }

      onFailure(error);
    } finally {
      setIsLoading(false);
    }
  });

  return {
    onSubmit,
    setValue,
    ...form,
    isLoading,
    authError,
    clearAuthError,
    highlightProviders,
    clearHighlights,
    showSignupCta,
    clearSignupCta,
  };
};
